import {parseAxiosPromise} from '@/axiosInstance'
import {QUERY_KEYS} from '@/queryClient'
import {useInfiniteQuery, UseInfiniteQueryOptions} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {infiniteQueryFetchNextPage} from '@/utilities/helpers'
import {useInputSearch} from '@/hooks/useInputSearch'
import {httpGetMunicipalities, HttpGetMunicipalitiesOptions} from './address.http'
import {z} from 'zod'
import {Municipality, PaginationResponse} from '@/types'

export const HttpGetMunicipalitiesResponse = z.object({
    data: z.array(Municipality),
    meta: PaginationResponse
})
export type HttpGetMunicipalitiesResponse = z.infer<typeof HttpGetMunicipalitiesResponse>

export const useGetMunicipalities = (
    promiseArgs: Omit<HttpGetMunicipalitiesOptions, 'page'>,
    options?: Pick<UseInfiniteQueryOptions<HttpGetMunicipalitiesResponse>, 'enabled'>
) => {
    const {searchValue, onSearch, onResetSearch} = useInputSearch()

    const query = useInfiniteQuery({
        queryKey: [QUERY_KEYS.MUNICIPALITIES, searchValue, promiseArgs],
        queryFn: ({pageParam = 1}) =>
            parseAxiosPromise({
                axiosPromise: httpGetMunicipalities({
                    page: pageParam,
                    search: searchValue,
                    limit: promiseArgs.limit
                }),
                responseShape: HttpGetMunicipalitiesResponse,
                onZodError: captureException
            }),
        ...options,
        initialPageParam: 1,
        getNextPageParam: ({meta}) => (meta.currentPage < meta.lastPage ? meta.currentPage + 1 : null)
    })

    return {
        ...query,
        fetchNextPage: () => infiniteQueryFetchNextPage(query),
        remappedData: query.data?.pages[0]?.data?.length ? query.data.pages.flatMap(page => page.data) : [],
        searchValue,
        onResetSearch,
        onSearch
    }
}
